"use client";

import ChairMaterialFields from "@/components/listing/ChairMaterialFields";
import { SpecOptionPills } from "@/components/listing/FurnitureOptionalDimensionsFields";
import {
  CHAIR_PER_SEAT_MAX,
  CHAIR_PER_SEAT_MIN,
  CHAIR_SET_MAX,
  CHAIR_SET_MIN,
  CHAIR_TOTAL_CAPACITY_MAX,
  SOFA_SHAPES,
  formatSofaShapeLabel,
  normalizeChairSetCount,
  resizeChairCapacitiesForSetCount,
  sofaUsesSimplifiedLShapedSeating,
  sumChairCapacities,
  type ChairQuantityMode,
  type ChairSpecFormState,
  type SeatingSpecUiVariant,
  type SofaShape,
} from "@/lib/listing/chairListingSpec";
import { cn } from "@/lib/utils";

interface ChairSpecFieldsProps {
  value: ChairSpecFormState;
  onChange: (next: ChairSpecFormState) => void;
  className?: string;
  variant?: SeatingSpecUiVariant;
  /** When true (Sofa), collect standard vs L-shaped before seating. */
  showSofaShape?: boolean;
}

const CAPACITY_OPTIONS = Array.from(
  { length: CHAIR_PER_SEAT_MAX - CHAIR_PER_SEAT_MIN + 1 },
  (_, i) => i + CHAIR_PER_SEAT_MIN,
);

function seatingCopy(variant: SeatingSpecUiVariant) {
  const unit = variant === "sofa" ? "sofa" : "chair";
  const units = variant === "sofa" ? "sofas" : "chairs";
  return {
    singleLabel: variant === "sofa" ? "Single sofa" : "Single chair",
    setLabel: variant === "sofa" ? "Set of sofas" : "Set of chairs",
    countQuestion: variant === "sofa" ? "How many sofas?" : "How many chairs?",
    countOption: (count: number) =>
      variant === "sofa" ? `${count} sofas` : `${count} chairs`,
    itemLabel: (index: number) =>
      variant === "sofa" ? `Sofa ${index + 1}` : `Chair ${index + 1}`,
    unit,
    units,
  };
}

export default function ChairSpecFields({
  value,
  onChange,
  className = "",
  variant = "chair",
  showSofaShape = false,
}: ChairSpecFieldsProps) {
  const copy = seatingCopy(variant);
  const isLShapedSofa =
    showSofaShape &&
    sofaUsesSimplifiedLShapedSeating("sofa-couche", value.sofaShape);

  const setMode = (mode: ChairQuantityMode) => {
    if (mode === "single") {
      onChange({
        ...value,
        chairQuantityMode: "single",
        chairSeatCapacities: [value.chairSeatCapacities[0] ?? null],
      });
      return;
    }
    const count = normalizeChairSetCount(value.chairSetCount);
    onChange({
      ...value,
      chairQuantityMode: "set",
      chairSetCount: count,
      chairSeatCapacities: resizeChairCapacitiesForSetCount(
        value.chairSeatCapacities,
        count,
      ),
    });
  };

  const setSofaShape = (shape: SofaShape) => {
    if (shape === "l_shaped") {
      onChange({
        ...value,
        sofaShape: shape,
        chairQuantityMode: "single",
        chairSeatCapacities: [value.chairSeatCapacities[0] ?? null],
      });
      return;
    }
    onChange({ ...value, sofaShape: shape });
  };

  const setSetCount = (count: number) => {
    const normalized = normalizeChairSetCount(count);
    onChange({
      ...value,
      chairSetCount: normalized,
      chairSeatCapacities: resizeChairCapacitiesForSetCount(
        value.chairSeatCapacities,
        normalized,
      ),
    });
  };

  const setCapacityAt = (index: number, capacity: number | null) => {
    const next = [...value.chairSeatCapacities];
    next[index] = capacity;
    onChange({ ...value, chairSeatCapacities: next });
  };

  const filledCapacities = value.chairSeatCapacities.filter(
    (c): c is number => c != null,
  );
  const totalCapacity =
    value.chairQuantityMode === "set"
      ? sumChairCapacities(
          value.chairSeatCapacities
            .slice(0, normalizeChairSetCount(value.chairSetCount))
            .filter((c): c is number => c != null),
        )
      : (value.chairSeatCapacities[0] ?? 0);
  const totalOverLimit =
    value.chairQuantityMode === "set" &&
    filledCapacities.length > 0 &&
    totalCapacity > CHAIR_TOTAL_CAPACITY_MAX;

  const showSingleCapacity =
    isLShapedSofa || value.chairQuantityMode === "single";
  const showSetCapacity = !isLShapedSofa && value.chairQuantityMode === "set";

  return (
    <div className={cn("space-y-6", className)}>
      {showSofaShape ? (
        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground">
            Sofa shape <span className="text-red-500">*</span>
          </p>
          <SpecOptionPills
            options={[...SOFA_SHAPES]}
            selected={value.sofaShape || ""}
            onSelect={setSofaShape}
            formatLabel={formatSofaShapeLabel}
          />
          <p className="text-xs text-text-light">
            L-shaped is a type of sofa — not a separate category.
          </p>
        </div>
      ) : null}

      {!isLShapedSofa ? (
        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground">
            Listing type <span className="text-red-500">*</span>
          </p>
          <div className="flex flex-wrap gap-2">
            {(
              [
                ["single", copy.singleLabel],
                ["set", copy.setLabel],
              ] as const
            ).map(([mode, label]) => {
              const selected = value.chairQuantityMode === mode;
              return (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setMode(mode)}
                  className={cn(
                    "rounded-lg border px-3 py-2 text-sm font-medium transition",
                    selected
                      ? "border-primary bg-primary-50 text-primary-800"
                      : "border-border bg-surface text-foreground hover:border-border-strong",
                  )}
                  aria-pressed={selected}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {showSingleCapacity && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground">
            Seating capacity
            {isLShapedSofa ? (
              <span className="ml-1 font-normal text-text-light">
                (optional)
              </span>
            ) : (
              <span className="text-red-500"> *</span>
            )}
          </p>
          <CapacityPills
            selected={value.chairSeatCapacities[0]}
            allowClear={isLShapedSofa}
            onSelect={(capacity) => setCapacityAt(0, capacity)}
          />
        </div>
      )}

      {showSetCapacity && (
        <div className="space-y-4">
          <div className="space-y-2">
            <label
              htmlFor="chair-set-count"
              className="block text-sm font-medium text-foreground"
            >
              {copy.countQuestion} <span className="text-red-500">*</span>
            </label>
            <select
              id="chair-set-count"
              value={normalizeChairSetCount(value.chairSetCount)}
              onChange={(e) => setSetCount(Number(e.target.value))}
              className="w-full max-w-xs rounded-lg border border-border px-3 py-2 text-sm"
            >
              {Array.from(
                { length: CHAIR_SET_MAX - CHAIR_SET_MIN + 1 },
                (_, i) => i + CHAIR_SET_MIN,
              ).map((count) => (
                <option key={count} value={count}>
                  {copy.countOption(count)}
                </option>
              ))}
            </select>
          </div>

          {Array.from({
            length: normalizeChairSetCount(value.chairSetCount),
          }).map((_, index) => (
            <div key={index} className="space-y-2">
              <p className="text-sm font-medium text-foreground">
                {copy.itemLabel(index)} — seats{" "}
                <span className="text-red-500">*</span>
              </p>
              <CapacityPills
                selected={value.chairSeatCapacities[index] ?? null}
                onSelect={(capacity) => setCapacityAt(index, capacity)}
              />
            </div>
          ))}

          <p
            className={cn(
              "text-xs",
              totalOverLimit ? "text-red-600" : "text-text-light",
            )}
          >
            Total seated capacity: {totalCapacity} / {CHAIR_TOTAL_CAPACITY_MAX}
            {totalOverLimit
              ? " — reduce capacities so the total is at most 12."
              : ""}
          </p>
        </div>
      )}

      <ChairMaterialFields value={value} onChange={onChange} />
    </div>
  );
}

function CapacityPills({
  selected,
  onSelect,
  allowClear = false,
}: {
  selected: number | null;
  onSelect: (capacity: number | null) => void;
  allowClear?: boolean;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {CAPACITY_OPTIONS.map((capacity) => {
        const isSelected = selected === capacity;
        return (
          <button
            key={capacity}
            type="button"
            onClick={() => onSelect(allowClear && isSelected ? null : capacity)}
            className={cn(
              "min-w-[2.5rem] rounded-lg border px-3 py-2 text-sm font-medium transition",
              isSelected
                ? "border-primary bg-primary-50 text-primary-800"
                : "border-border bg-surface text-foreground hover:border-border-strong",
            )}
            aria-pressed={isSelected}
          >
            {capacity}
          </button>
        );
      })}
    </div>
  );
}
